import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getLanguageDirection, SUPPORTED_LANGUAGES } from "../i18n";
import { NUMBER_LOCALE } from "../utils/format";

function useLocale() {
  const { i18n, t } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "en").startsWith(
    "ar",
  )
    ? "ar"
    : "en";
  const direction = getLanguageDirection(language);

  const changeLanguage = useCallback(
    (nextLanguage) => {
      const safeLanguage = SUPPORTED_LANGUAGES.some(
        (item) => item.code === nextLanguage,
      )
        ? nextLanguage
        : "en";

      return i18n.changeLanguage(safeLanguage);
    },
    [i18n],
  );

  // Same separators in both languages ("." decimal, "," grouping): ar-LY
  // swaps them, so 10.000 would read as ten thousand (browser acceptance N1).
  const formatNumber = useCallback(
    (value, options) => new Intl.NumberFormat(NUMBER_LOCALE, options).format(value),
    [],
  );

  const formatDate = useCallback(
    (value, options) => {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return new Intl.DateTimeFormat(language === "ar" ? "ar-LY" : "en-US", {
        dateStyle: "medium",
        timeStyle: "short",
        ...options,
      }).format(date);
    },
    [language],
  );

  const value = useMemo(
    () => ({
      t,
      language,
      direction,
      isRTL: direction === "rtl",
      languages: SUPPORTED_LANGUAGES,
      changeLanguage,
      formatNumber,
      formatDate,
    }),
    [changeLanguage, direction, formatDate, formatNumber, language, t],
  );

  return value;
}

export default useLocale;
